import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Box,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BuildIcon from '@mui/icons-material/Build';
import UpdateIcon from '@mui/icons-material/Update';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import Button from '@mui/material/Button';
import { db } from '../../firebase/config';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationService } from '../../services/NotificationService.js';
import { NOTIFICATION_TYPES, NOTIFICATION_STATUS } from '../../models/Notification';
import { COLLECTIONS } from '../../firebase/collections';

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [error, setError] = useState(null);
  const { currentUser, userService, userRole } = useAuth();

  const notificationId = userService?.id || currentUser?.uid;

  // Écouter les notifications non lues en temps réel via onSnapshot
  useEffect(() => {
    if (!notificationId) return;

    const q = query(
      collection(db, COLLECTIONS.NOTIFICATIONS),
      where('serviceId', '==', notificationId),
      where('status', '==', NOTIFICATION_STATUS.UNREAD),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setNotifications(notifs);
        setError(null);
      },
      (err) => {
        console.error('NotificationBell: Erreur onSnapshot', err);
        setError('Erreur lors du chargement des notifications');
      }
    );

    // Nettoyage : désabonnement au démontage ou changement d'utilisateur
    return () => unsubscribe();
  }, [notificationId]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleResetNotifications = async () => {
    try {
      await NotificationService.resetAllNotifications(notificationId);
      // onSnapshot mettra à jour automatiquement l'état
    } catch (err) {
      console.error('NotificationBell: Erreur lors de la réinitialisation', err);
      setError('Erreur lors de la réinitialisation des notifications');
    } finally {
      handleClose();
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      await NotificationService.markAsRead(notification.id);
      // onSnapshot retire automatiquement la notification de la liste

      if (notification.workRequestId) {
        navigate(`/demandes?requestId=${notification.workRequestId}`);
      }
    } catch (err) {
      console.error('NotificationBell: Erreur lors du traitement de la notification', err);
      setError('Erreur lors du traitement de la notification');
    } finally {
      handleClose();
    }
  };

  const formatNotificationDate = (timestamp) => {
    try {
      if (!timestamp) return '';
      const date = timestamp.toDate ? timestamp.toDate() : timestamp;
      const diffMinutes = Math.floor((Date.now() - date.getTime()) / (1000 * 60));

      if (diffMinutes < 1) return 'À l\'instant';
      if (diffMinutes < 60) return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;

      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return '';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.NEW_REQUEST:
        return <BuildIcon fontSize="small" />;
      case NOTIFICATION_TYPES.STATUS_CHANGE:
        return <UpdateIcon fontSize="small" />;
      default:
        return <NotificationsIcon fontSize="small" />;
    }
  };

  if (!notificationId) return null;

  return (
    <>
      <Tooltip title={notifications.length > 0 ? `${notifications.length} notification${notifications.length > 1 ? 's' : ''} non lue${notifications.length > 1 ? 's' : ''}` : 'Aucune notification'} arrow>
        <IconButton color="inherit" onClick={handleClick} sx={{ ml: 1 }}>
          <Badge badgeContent={notifications.length || 0} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ sx: { maxHeight: 400, width: '350px', overflow: 'auto' } }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Notifications</Typography>
          <Tooltip title="Supprimer toutes vos notifications" arrow>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={handleResetNotifications}
            >
              Réinitialiser
            </Button>
          </Tooltip>
        </Box>

        {error && (
          <MenuItem disabled>
            <Typography color="error">{error}</Typography>
          </MenuItem>
        )}

        {notifications.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Aucune notification non lue
            </Typography>
          </MenuItem>
        ) : (
          notifications.map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <ListItemIcon>
                {getNotificationIcon(notification.type)}
              </ListItemIcon>
              <ListItemText
                primary={notification.message}
                secondary={
                  <Box component="span" sx={{ display: 'block' }}>
                    {(() => {
                      const d = notification.details || {};
                      const isSet = (v) => v && v !== 'Non spécifié' && v !== 'Bâtiment non spécifié';
                      if (isSet(d.building)) {
                        return (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {d.building}{isSet(d.room) ? ` - ${d.room}` : ''}
                          </Typography>
                        );
                      }
                      if (isSet(d.problem)) {
                        return (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {d.problem}{isSet(d.priority) ? ` • ${d.priority}` : ''}
                          </Typography>
                        );
                      }
                      if (isSet(d.title)) {
                        return (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {d.title}
                          </Typography>
                        );
                      }
                      return null;
                    })()}
                    <Typography variant="caption" color="text.secondary">
                      {formatNotificationDate(notification.createdAt)}
                    </Typography>
                  </Box>
                }
              />
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
